// Angular Modules
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ModuleWithProviders,
  NgModule,
  NO_ERRORS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Third Party Modules
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxFileDropModule } from 'ngx-file-drop';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { ClipboardModule } from 'ngx-clipboard';

// Material Modules
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatNativeDateModule } from '@angular/material/core';

// LC Modules
import { PromptDialogService as CorePromptDialogService } from '@lc/core';

// Internal Services
import {
  AgentSearchModule,
  CardsModule,
  DialogsModule,
  FormButtonsModule,
  HeaderModule,
  InputsModule,
  UICoreModule,
} from '@lc/ui';
import { PromptDialogService } from './dialogs/prompt-dialog/prompt-dialog.service';
import { NotesDialogService } from './dialogs/notes-dialog/notes-dialog.service';
import { PdfPreviewDialogService } from './dialogs/pdf-preview-dialog/pdf-preview-dialog.service';
import { UploadResultsDialogService } from './dialogs/upload-results-dialog/upload-results-dialog.service';
import { PhotoGalleryDialogService } from './dialogs/photo-gallery-dialog/photo-gallery-dialog.service';
import { ReasonDialogService } from './dialogs/reason-dialog/reason-dialog.service';
import { BulkAssignDialogService } from './dialogs/bulk-assign-dialog/bulk-assign-dialog.service';

// Internal Components
import { PromptDialogComponent } from './dialogs/prompt-dialog/prompt-dialog.component';
import { NotesDialogComponent } from './dialogs/notes-dialog/notes-dialog.component';
import { FieldVersionComponent } from './dialogs/versions/field-version.component';
import { PdfPreviewDialogComponent } from './dialogs/pdf-preview-dialog/pdf-preview.dialog.component';
import { BulkAssignDialogComponent } from './dialogs/bulk-assign-dialog/bulk-assign-dialog.component';
import { PhotoGalleryDialogComponent } from './dialogs/photo-gallery-dialog/photo-gallery-dialog.component';
import { UploadResultsDialogComponent } from './dialogs/upload-results-dialog/upload-results-dialog.component';
import { ReasonDialogComponent } from './dialogs/reason-dialog/reason-dialog.component';

import { BrowserSupportComponent } from './browser/browser-support.component';
import { PhotoComponent } from './photos/photo-list/photo/photo.component';

const angularModules = [CommonModule, FormsModule, ReactiveFormsModule, RouterModule];

const thirdPartyModules = [
  NgxFileDropModule, NgxMaskDirective, ImageCropperComponent,
  ClipboardModule,
];
const materialModules = [
  MatIconModule, MatSelectModule, MatChipsModule, MatAutocompleteModule, MatDialogModule, MatDividerModule,
  MatButtonModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatNativeDateModule, MatTableModule,
];
const services = [PdfPreviewDialogService, UploadResultsDialogService, PhotoGalleryDialogService, ReasonDialogService, BulkAssignDialogService];
const dialogComponents = [PromptDialogComponent, NotesDialogComponent, FieldVersionComponent, BulkAssignDialogComponent, PhotoGalleryDialogComponent, UploadResultsDialogComponent, ReasonDialogComponent, PdfPreviewDialogComponent];
const inputComponents = [];
const internalComponents = [...dialogComponents, ...inputComponents];

// WARNING: DO NOT IMPORT CORE MODULE HERE
@NgModule({
  imports: [
    ...angularModules,
    ...thirdPartyModules,
    ...materialModules,
    InputsModule,
    UICoreModule,
    CardsModule,
    HeaderModule,
    DialogsModule,
    FormButtonsModule,
    AgentSearchModule,
    PhotoComponent,
    RouterModule.forChild([
      { path: 'browser-support', component: BrowserSupportComponent },
    ]),
    ...internalComponents,
    BrowserSupportComponent,
  ],
  providers: [
    ...services,
    provideNgxMask(),
  ],
  exports: [
    ...internalComponents,
    BrowserSupportComponent,
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA,
  ],
})
export class SharedComponentsUiModule {
  static forRoot(): ModuleWithProviders<SharedComponentsUiModule> {
    return {
      ngModule: SharedComponentsUiModule,
      providers: [
        // Registers the concrete implementation of the PromptDialogService for use throughout the application.
        // Overrides the core implementation which is just an empty class, similiar to an interface
        { provide: CorePromptDialogService, useClass: PromptDialogService },
      ],
    };
  }
}
